/**
 * BrandsCarousel — lightweight, scroll-snap-based logo carousel.
 * Auto-discovers all [data-section-id] elements inside .brands-carousel-container.
 * Supports multiple instances on a single page.
 *
 * Mobile auto-slide: controlled by data-mobile-autoplay / data-mobile-autoplay-speed
 * attributes on the section wrapper (set from the section schema). Autoplay only
 * ever runs below the 749px breakpoint — desktop behavior is completely unchanged.
 */
(function () {
  'use strict';

  /* ── Instance registry ── */
  var instances = {};

  class BrandsCarousel {
    constructor(sectionId) {
      this.sectionId = sectionId;
      this.container = document.getElementById('BrandsCarousel-' + sectionId);
      this.track = document.getElementById('BrandsTrack-' + sectionId);

      if (!this.container || !this.track) return;

      this.prevBtn = this.container.querySelector('[data-direction="prev"]');
      this.nextBtn = this.container.querySelector('[data-direction="next"]');
      this.items = [];
      this.resizeObserver = null;
      this.scrollTimeout = null;

      /* ── Mobile autoplay state ── */
      this.wrapper = document.querySelector(
        '.brands-section-wrapper[data-section-id="' + sectionId + '"]'
      );
      this.autoplayEnabled = !!this.wrapper && this.wrapper.dataset.mobileAutoplay === 'true';
      this.autoplaySpeed =
        (parseFloat(this.wrapper && this.wrapper.dataset.mobileAutoplaySpeed) || 3) * 1000;
      this.autoplayTimer = null;
      this.resumeTimeout = null;
      this.mobileMediaQuery = window.matchMedia('(max-width: 749px)');

      this._onScroll = this._onScroll.bind(this);
      this._onPrev = this._onPrev.bind(this);
      this._onNext = this._onNext.bind(this);
      this._onLoad = this._onLoad.bind(this);
      this._onMediaChange = this._onMediaChange.bind(this);
      this._onAutoplayTick = this._onAutoplayTick.bind(this);
      this._maybeStartAutoplay = this._maybeStartAutoplay.bind(this);
      this._pauseAutoplayTemporarily = this._pauseAutoplayTemporarily.bind(this);

      this.init();
    }

    /** Initialise the carousel: cache items, bind events, measure. */
    init() {
      this.items = Array.from(this.track.querySelectorAll('.brands-item'));
      if (this.items.length === 0) return;

      this._bindEvents();
      this._measure();
      this._maybeStartAutoplay();
    }

    /** Bind click, scroll, resize, and load listeners. */
    _bindEvents() {
      if (this.prevBtn) this.prevBtn.addEventListener('click', this._onPrev);
      if (this.nextBtn) this.nextBtn.addEventListener('click', this._onNext);

      this.track.addEventListener('scroll', this._onScroll, { passive: true });

      /* Re-measure on window load when all assets are ready. */
      window.addEventListener('load', this._onLoad);

      /* Re-measure when any image inside the track loads. */
      var images = this.track.querySelectorAll('img');
      for (var i = 0; i < images.length; i++) {
        if (!images[i].complete) {
          images[i].addEventListener('load', this._onLoad);
        }
      }

      /* ResizeObserver re-measures when the track width changes. */
      if (typeof ResizeObserver !== 'undefined') {
        this.resizeObserver = new ResizeObserver(
          function () {
            this._measure();
          }.bind(this)
        );
        this.resizeObserver.observe(this.track);
      }

      /* Autoplay: only relevant on mobile, only if the merchant enabled it. */
      if (this.autoplayEnabled) {
        if (this.mobileMediaQuery.addEventListener) {
          this.mobileMediaQuery.addEventListener('change', this._onMediaChange);
        } else {
          this.mobileMediaQuery.addListener(this._onMediaChange); // older Safari fallback
        }

        /* Pause on user interaction, resume after one autoplay interval of idle time. */
        this.track.addEventListener('touchstart', this._pauseAutoplayTemporarily, { passive: true });
        this.track.addEventListener('mouseenter', this._pauseAutoplayTemporarily);
        if (this.prevBtn) this.prevBtn.addEventListener('click', this._pauseAutoplayTemporarily);
        if (this.nextBtn) this.nextBtn.addEventListener('click', this._pauseAutoplayTemporarily);
      }
    }

    /** Called when window or images load. */
    _onLoad() {
      this._measure();
    }

    /**
     * Core measurement — decide whether arrows are needed.
     * Compares scrollWidth (total content) vs clientWidth (visible area).
     */
    _measure() {
      var overflows = this.track.scrollWidth > this.track.clientWidth + 1;

      if (overflows) {
        this.container.classList.remove('brands-arrows-hidden');
      } else {
        this.container.classList.add('brands-arrows-hidden');
      }

      this._updateArrows();
    }

    _updateArrows() {
      var scrollLeft = Math.round(this.track.scrollLeft);
      var maxScroll = this.track.scrollWidth - this.track.clientWidth;

      if (this.prevBtn) {
        if (scrollLeft <= 10) {
          this.prevBtn.disabled = true;
          this.prevBtn.classList.add('is-disabled');
        } else {
          this.prevBtn.disabled = false;
          this.prevBtn.classList.remove('is-disabled');
        }
      }
      if (this.nextBtn) {
        if (scrollLeft >= maxScroll - 10) {
          this.nextBtn.disabled = true;
          this.nextBtn.classList.add('is-disabled');
        } else {
          this.nextBtn.disabled = false;
          this.nextBtn.classList.remove('is-disabled');
        }
      }
    }

    /** Scroll handler — debounced arrow-state update. */
    _onScroll() {
      if (this.scrollTimeout) clearTimeout(this.scrollTimeout);
      this.scrollTimeout = setTimeout(
        function () {
          this._updateArrows();
        }.bind(this),
        50
      );
    }

    /**
     * Navigate to the previous logo item.
     * Finds the first item whose left edge is before the current scroll position,
     * then scrolls to it.
     */
    _onPrev() {
      var trackRect = this.track.getBoundingClientRect();
      var trackLeft = trackRect.left;
      var target = null;

      /* Walk items in reverse to find the first one whose left edge is
         before the track's visible left edge (with 2px tolerance). */
      for (var i = this.items.length - 1; i >= 0; i--) {
        var itemRect = this.items[i].getBoundingClientRect();
        if (itemRect.left < trackLeft - 2) {
          target = this.items[i];
          break;
        }
      }

      if (target) {
        /* Scroll so the target aligns to the start of the track. */
        var offset = target.offsetLeft;
        this.track.scrollTo({ left: offset, behavior: 'smooth' });
      } else {
        /* Already at the start — snap to 0. */
        this.track.scrollTo({ left: 0, behavior: 'smooth' });
      }
    }

    /**
     * Navigate to the next logo item.
     * Finds the first item whose right edge extends beyond the track's
     * visible right edge, then scrolls so it becomes fully visible.
     */
    _onNext() {
      var trackRect = this.track.getBoundingClientRect();
      var trackRight = trackRect.right;
      var target = null;

      /* Walk items forward to find the first one whose right edge is
         past the track's visible right edge (with 2px tolerance). */
      for (var i = 0; i < this.items.length; i++) {
        var itemRect = this.items[i].getBoundingClientRect();
        if (itemRect.right > trackRight + 2) {
          target = this.items[i];
          break;
        }
      }

      if (target) {
        var offset = target.offsetLeft;
        var maxScroll = this.track.scrollWidth - this.track.clientWidth;
        /* Prevent scrolling past the end — clamp to max. */
        this.track.scrollTo({
          left: Math.min(offset, maxScroll),
          behavior: 'smooth',
        });
      }
    }

    /** Media query change handler — start/stop autoplay when crossing the mobile breakpoint. */
    _onMediaChange(e) {
      if (e.matches) {
        this._maybeStartAutoplay();
      } else {
        this._stopAutoplay();
      }
    }

    /** Start autoplay if enabled, currently on mobile, and there's more than one item. */
    _maybeStartAutoplay() {
      if (!this.autoplayEnabled) return;
      if (!this.mobileMediaQuery.matches) return;
      if (this.items.length <= 1) return;

      this._stopAutoplay();
      this.autoplayTimer = setInterval(this._onAutoplayTick, this.autoplaySpeed);
    }

    _stopAutoplay() {
      if (this.autoplayTimer) {
        clearInterval(this.autoplayTimer);
        this.autoplayTimer = null;
      }
    }

    /** Pause on interaction, then resume automatically after one autoplay interval. */
    _pauseAutoplayTemporarily() {
      if (!this.autoplayEnabled) return;
      this._stopAutoplay();
      clearTimeout(this.resumeTimeout);
      this.resumeTimeout = setTimeout(this._maybeStartAutoplay, this.autoplaySpeed);
    }

    /** Advance one item; loop back to the start when at the end. */
    _onAutoplayTick() {
      var maxScroll = this.track.scrollWidth - this.track.clientWidth;
      var scrollLeft = Math.round(this.track.scrollLeft);

      if (scrollLeft >= maxScroll - 10) {
        this.track.scrollTo({ left: 0, behavior: 'smooth' });
      } else {
        this._onNext();
      }
    }

    /** Tear down all event listeners and observers. */
    destroy() {
      if (this.prevBtn) this.prevBtn.removeEventListener('click', this._onPrev);
      if (this.nextBtn) this.nextBtn.removeEventListener('click', this._onNext);
      this.track.removeEventListener('scroll', this._onScroll);
      window.removeEventListener('load', this._onLoad);

      var images = this.track.querySelectorAll('img');
      for (var i = 0; i < images.length; i++) {
        images[i].removeEventListener('load', this._onLoad);
      }

      if (this.resizeObserver) this.resizeObserver.disconnect();
      if (this.scrollTimeout) clearTimeout(this.scrollTimeout);

      /* Autoplay cleanup */
      this._stopAutoplay();
      clearTimeout(this.resumeTimeout);
      if (this.mobileMediaQuery.removeEventListener) {
        this.mobileMediaQuery.removeEventListener('change', this._onMediaChange);
      } else if (this.mobileMediaQuery.removeListener) {
        this.mobileMediaQuery.removeListener(this._onMediaChange);
      }
      this.track.removeEventListener('touchstart', this._pauseAutoplayTemporarily);
      this.track.removeEventListener('mouseenter', this._pauseAutoplayTemporarily);
      if (this.prevBtn) this.prevBtn.removeEventListener('click', this._pauseAutoplayTemporarily);
      if (this.nextBtn) this.nextBtn.removeEventListener('click', this._pauseAutoplayTemporarily);
    }
  }

  /* ── Bootstrap — auto-discover all brands sections ── */
  function initSection(sectionEl) {
    var sectionId = sectionEl.dataset.sectionId;
    if (!sectionId) return;
    if (instances[sectionId]) instances[sectionId].destroy();
    instances[sectionId] = new BrandsCarousel(sectionId);
  }

  function initAll() {
    var sections = document.querySelectorAll('.brands-section-wrapper[data-section-id]');
    sections.forEach(initSection);
  }

  /* Run on DOMContentLoaded or immediately if already loaded. */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  /* ── Shopify Theme Editor events ── */
  if (typeof Shopify !== 'undefined' && Shopify.designMode) {
    document.addEventListener('shopify:section:load', function (e) {
      var sectionEl = e.target.querySelector('.brands-section-wrapper[data-section-id]');
      if (sectionEl) initSection(sectionEl);
    });

    document.addEventListener('shopify:section:unload', function (e) {
      var sectionId = e.detail.sectionId;
      if (instances[sectionId]) {
        instances[sectionId].destroy();
        delete instances[sectionId];
      }
    });

    document.addEventListener('shopify:block:select', function (e) {
      var sectionId = e.detail.sectionId;
      if (!instances[sectionId]) return;

      /* Scroll to the selected block in the editor. */
      var block = e.target;
      if (block && block.closest('.brands-track')) {
        block.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
      }
      /* Re-measure in case layout shifted. */
      instances[sectionId]._measure();
    });

    /* Re-initialise when blocks are added or removed. */
    var blockChangeHandler = function (e) {
      var sectionId = e.detail.sectionId;
      if (!instances[sectionId]) return;

      /* Small delay to let Shopify finish DOM mutations. */
      setTimeout(function () {
        var sectionEl = document.querySelector(
          '.brands-section-wrapper[data-section-id="' + sectionId + '"]'
        );
        if (sectionEl) initSection(sectionEl);
      }, 100);
    };

    document.addEventListener('shopify:block:add', blockChangeHandler);
    document.addEventListener('shopify:block:remove', blockChangeHandler);
  }
})();